import { Heading, Label as LabelOG, Paragraph as ParagraphOG, SizableText as SizableTextOG, styled, Text as TextOG } from "tamagui";
import { headingLineHeight, subheadlineLineHeight } from "../config/fonts";
import { displayLineHeightCompensation } from "../themes/token-fontSizes";

// =============================================================================
// LEGACY Typography System (Deprecated)
// These components use the old numeric font size tokens ($1-$14)
// =============================================================================

const commonVariants = {
  bold: {
    true: {
      fontWeight: "500"
    }
  },
  italic: {
    true: {
      fontStyle: "italic"
    }
  }
} as const;

/**
 * @deprecated Use V2 Typography components (Display, Title, Subtitle, Body, LegalText) instead
 */
export const Text = styled(TextOG, {
  variants: {
    ...commonVariants
  } as const
});

/**
 * @deprecated Use V2 Typography components (Display, Title, Subtitle, Body, LegalText) instead
 */
export const SizableText = styled(SizableTextOG, {
  variants: {
    ...commonVariants
  } as const
});

/**
 * @deprecated Use Display1-Display5 components instead
 */
export const Headline = styled(Heading, {
  name: "Headline",
  tag: "p",
  size: "$9",
  variants: {
    ...commonVariants,
    size: {
      "...size": (val, {
        font
      }) => {
        return {
          marginTop: -1 * (
          // @ts-expect-error Tamagui types aren't great here
          (font?.size?.[val]?.val as number) * (1.0 - headingLineHeight)),
          paddingTop:
          // @ts-expect-error Tamagui types aren't great here
          (font?.size?.[val]?.val as number) * (1.0 - headingLineHeight)
        };
      }
    }
  } as const
});

/**
 * @deprecated Use Display1 instead
 */
export const Headline1 = styled(Headline, {
  name: "Headline1",
  tag: "h1",
  fontFamily: "$heading",
  size: "$11",
  fontWeight: "500",
  $gtMd: {
    size: "$13"
  }
});

/**
 * @deprecated Use Title1-Title5 components instead
 */
export const Subheadline = styled(ParagraphOG, {
  name: "Subheadline",
  tag: "p",
  variants: {
    ...commonVariants,
    size: {
      "...size": (val, {
        font
      }) => {
        return {
          // @ts-expect-error Tamagui types aren't great here
          fontSize: font?.size?.[val]?.val as number,
          // @ts-expect-error Tamagui types aren't great here
          lineHeight: font?.size?.[val]?.val * subheadlineLineHeight
        };
      }
    }
  } as const,
  defaultVariants: {
    size: "$9"
  }
});

/**
 * @deprecated Use Title1 instead
 */
export const Subheadline1 = styled(Subheadline, {
  name: "Subheadline1",
  tag: "h2",
  size: "$10"
});

/**
 * @deprecated Use Title2 instead
 */
export const Subheadline2 = styled(Subheadline, {
  name: "Subheadline2",
  tag: "h3",
  size: "$9"
});

/**
 * @deprecated Use Title3 instead
 */
export const Subheadline3 = styled(Subheadline, {
  name: "Subheadline3",
  tag: "h4",
  size: "$8"
});

/**
 * @deprecated Use Title4 instead
 */
export const Subheadline4 = styled(Subheadline, {
  name: "Subheadline4",
  tag: "h5",
  size: "$7"
});

/**
 * @deprecated Use Title5 instead
 */
export const Subheadline5 = styled(Subheadline, {
  name: "Subheadline5",
  size: "$6",
  fontWeight: "400"
});

/**
 * @deprecated Use Body1, Body2, or Body3 instead
 */
export const Paragraph = styled(ParagraphOG, {
  name: "Paragraph",
  tag: "p",
  variants: {
    ...commonVariants
  } as const
});

/**
 * @deprecated Use Subtitle1 instead
 */
export const Title = styled(Paragraph, {
  name: "Title",
  size: "$5"
});

/**
 * @deprecated Use Body2 instead
 */
export const Paragraph2 = styled(Paragraph, {
  name: "Paragraph2",
  size: "$3"
});

/**
 * @deprecated Use Body3 instead
 */
export const ParagraphSmall = styled(Paragraph, {
  name: "ParagraphSmall",
  size: "$2"
});

/**
 * @deprecated Use Subtitle1, Subtitle2, or Subtitle3 instead
 */
export const Label = styled(LabelOG, {
  fontFamily: "$body",
  variants: {
    ...commonVariants
  } as const
});

/**
 * @deprecated Create a new anchor component using the V2 typography system
 */
export const AnchorText = styled(TextOG, {
  name: "Anchor",
  tag: "a",
  accessibilityRole: "link",
  color: "$navy",
  textDecorationLine: "none",
  fontWeight: "500",
  hoverStyle: {
    textDecorationLine: "underline"
  },
  variants: {
    ...commonVariants
  } as const
});

// Tighter line heights require paddingTop/marginTop compensation to prevent glyph clipping
export const Display1 = styled(SizableTextOG, {
  name: "Display1",
  tag: "h1",
  fontFamily: "$display",
  size: "$display1",
  fontWeight: "500",
  paddingTop: displayLineHeightCompensation.display1,
  marginTop: -displayLineHeightCompensation.display1
});
export const Display2 = styled(SizableTextOG, {
  name: "Display2",
  tag: "h2",
  fontFamily: "$display",
  size: "$display2",
  fontWeight: "500",
  paddingTop: displayLineHeightCompensation.display2,
  marginTop: -displayLineHeightCompensation.display2
});
export const Display3 = styled(SizableTextOG, {
  name: "Display3",
  tag: "h3",
  fontFamily: "$display",
  size: "$display3",
  fontWeight: "500",
  paddingTop: displayLineHeightCompensation.display3,
  marginTop: -displayLineHeightCompensation.display3
});
export const Display4 = styled(SizableTextOG, {
  name: "Display4",
  tag: "h4",
  fontFamily: "$display",
  size: "$display4",
  fontWeight: "500",
  paddingTop: displayLineHeightCompensation.display4,
  marginTop: -displayLineHeightCompensation.display4
});
export const Display5 = styled(SizableTextOG, {
  name: "Display5",
  tag: "h5",
  fontFamily: "$display",
  size: "$display5",
  fontWeight: "500",
  paddingTop: displayLineHeightCompensation.display5,
  marginTop: -displayLineHeightCompensation.display5
});
export const Title1 = styled(SizableTextOG, {
  name: "Title1",
  tag: "h1",
  fontFamily: "$text",
  size: "$title1",
  fontWeight: "500"
});
export const Title2 = styled(SizableTextOG, {
  name: "Title2",
  tag: "h2",
  fontFamily: "$text",
  size: "$title2",
  fontWeight: "500"
});
export const Title3 = styled(SizableTextOG, {
  name: "Title3",
  tag: "h3",
  fontFamily: "$text",
  size: "$title3",
  fontWeight: "500"
});
export const Title4 = styled(SizableTextOG, {
  name: "Title4",
  tag: "h4",
  fontFamily: "$text",
  size: "$title4",
  fontWeight: "500"
});
export const Title5 = styled(SizableTextOG, {
  name: "Title5",
  tag: "h5",
  fontFamily: "$text",
  size: "$title5",
  fontWeight: "500"
});
export const Subtitle1 = styled(SizableTextOG, {
  name: "Subtitle1",
  tag: "p",
  fontFamily: "$text",
  size: "$subtitle1",
  fontWeight: "500"
});
export const Subtitle2 = styled(SizableTextOG, {
  name: "Subtitle2",
  tag: "p",
  fontFamily: "$text",
  size: "$subtitle2",
  fontWeight: "500"
});
export const Subtitle3 = styled(SizableTextOG, {
  name: "Subtitle3",
  tag: "p",
  fontFamily: "$text",
  size: "$subtitle3",
  fontWeight: "500"
});
export const Body1 = styled(SizableTextOG, {
  name: "Body1",
  tag: "p",
  fontFamily: "$text",
  size: "$body1",
  fontWeight: "400"
});
export const Body2 = styled(SizableTextOG, {
  name: "Body2",
  tag: "p",
  fontFamily: "$text",
  size: "$body2",
  fontWeight: "400"
});
export const Body3 = styled(SizableTextOG, {
  name: "Body3",
  tag: "p",
  fontFamily: "$text",
  size: "$body3",
  fontWeight: "400"
});
export const LegalText = styled(SizableTextOG, {
  name: "LegalText",
  tag: "p",
  fontFamily: "$text",
  size: "$legal",
  fontWeight: "400"
});